import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { classes, ecrireStockage, effacerStockage, heureCourte, lireStockage } from '../outils/classes';
import { Bouton } from './Bouton';
import styles from './Formulaire.module.css';

const PREFIXE_BROUILLON = 'cc-brouillon:';

interface BrouillonStocke<T> {
  valeurs: T;
  enregistre_le: string;
}

/**
 * Brouillon enregistré automatiquement sur l'appareil (H9-03) : survit à une expiration de
 * session ou à une perte de connexion. Les champs secrets ne doivent jamais y figurer.
 */
export function useBrouillon<T>(cle: string | null, valeurs: T, options: { delaiMs?: number; actif?: boolean } = {}) {
  const { delaiMs = 800, actif = true } = options;
  const [enregistreLe, setEnregistreLe] = useState<Date | null>(null);
  const [disponible, setDisponible] = useState<BrouillonStocke<T> | null>(() =>
    cle ? lireStockage<BrouillonStocke<T>>(PREFIXE_BROUILLON + cle) : null,
  );
  const serialise = JSON.stringify(valeurs);
  const initial = useRef(serialise);

  useEffect(() => {
    if (!cle || !actif) return;
    // Tant que le formulaire est dans son état initial, on n'écrase pas un brouillon existant.
    if (serialise === initial.current) return;
    const minuterie = window.setTimeout(() => {
      const maintenant = new Date();
      if (ecrireStockage(PREFIXE_BROUILLON + cle, { valeurs: JSON.parse(serialise) as T, enregistre_le: maintenant.toISOString() })) {
        setEnregistreLe(maintenant);
        setDisponible(null);
      }
    }, delaiMs);
    return () => window.clearTimeout(minuterie);
  }, [cle, serialise, delaiMs, actif]);

  const effacer = useCallback(() => {
    if (cle) effacerStockage(PREFIXE_BROUILLON + cle);
    setEnregistreLe(null);
    setDisponible(null);
  }, [cle]);

  const ignorer = useCallback(() => setDisponible(null), []);

  return {
    enregistreLe,
    /** Brouillon trouvé au chargement, à proposer à la restauration. */
    brouillonDisponible: disponible?.valeurs ?? null,
    brouillonDate: disponible ? new Date(disponible.enregistre_le) : null,
    effacer,
    ignorer,
  };
}

export interface ErreurChamp {
  /** Identifiant du champ (attribut id) pour le lien du résumé. */
  champ: string;
  message: string;
}

export interface ProprietesFormulaire {
  onSoumettre: (e: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
  actions?: ReactNode;
  /** Résumé des erreurs affiché en tête, avec liens vers les champs. */
  erreurs?: ErreurChamp[];
  /** Date du dernier enregistrement automatique du brouillon. */
  brouillonEnregistreLe?: Date | null;
  /** Brouillon disponible : propose de le reprendre. */
  restauration?: { date: Date | null; onReprendre: () => void; onIgnorer: () => void } | null;
  libelle?: string;
  className?: string;
  id?: string;
}

export function Formulaire({
  onSoumettre,
  children,
  actions,
  erreurs,
  brouillonEnregistreLe,
  restauration,
  libelle,
  className,
  id,
}: ProprietesFormulaire) {
  const resume = useRef<HTMLDivElement>(null);
  const nbErreurs = erreurs?.length ?? 0;
  useEffect(() => {
    if (nbErreurs > 0) resume.current?.focus();
  }, [nbErreurs]);

  return (
    <form
      id={id}
      className={classes(styles.formulaire, className)}
      aria-label={libelle}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        onSoumettre(e);
      }}
    >
      {restauration ? (
        <div className={styles.restauration} role="status">
          <span>
            Un brouillon non envoyé existe sur cet appareil
            {restauration.date ? ` (enregistré à ${heureCourte(restauration.date)})` : ''}.
          </span>
          <Bouton petit variante="secondaire" onClick={restauration.onReprendre}>
            Reprendre le brouillon
          </Bouton>
          <Bouton petit variante="discret" onClick={restauration.onIgnorer}>
            Ignorer
          </Bouton>
        </div>
      ) : null}
      {nbErreurs > 0 ? (
        <div className={styles.resume} ref={resume} tabIndex={-1} role="alert">
          <h3>
            {nbErreurs === 1 ? 'Une erreur empêche l\'enregistrement' : `${nbErreurs} erreurs empêchent l'enregistrement`}
          </h3>
          <ul>
            {erreurs?.map((e) => (
              <li key={`${e.champ}-${e.message}`}>
                <a href={`#${e.champ}`}>{e.message}</a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {children}
      {actions || brouillonEnregistreLe ? (
        <div className={styles.actions}>
          {actions}
          {brouillonEnregistreLe ? (
            <span className={styles.brouillon} role="status">
              Brouillon enregistré à {heureCourte(brouillonEnregistreLe)}
            </span>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}

/** Grille de champs adaptée à la largeur. */
export function GrilleChamps({ children }: { children: ReactNode }) {
  return <div className={styles.grille}>{children}</div>;
}
